/** @format */

import {WidgetSettings} from 'components/PropertyWidget/model';
import {ChartIds} from 'pages/Home/DigitalCast/models/chartIds';
import {SavedDashboardsList} from 'pages/Home/DigitalCast/models/savedSpaceSettings';
import {SpaceId, SpaceList} from 'pages/Home/DigitalCast/models/spaces';

import {DcSettingsFromQuery} from '../../../models/api';

/**
 * Если данные есть, но настроек ещё нет, вернёт null
 * Если настройки есть, но они пустые, вернёт {}
 * В остальных случаях вернёт настройки пространств
 * @param json
 */

const parseSavedSpaces = (json: DcSettingsFromQuery | string): SpaceList | null => {
  let rawJson = '';

  if (typeof json === 'object') {
    rawJson = JSON.stringify(json);
  } else {
    rawJson = json || 'null';
  }

  const saved: SavedDashboardsList = JSON.parse(rawJson);

  if (!saved) {
    return null;
  }

  const savedSpaceIds = Object.keys(saved) as SpaceId[];

  if (!savedSpaceIds.length) {
    return {};
  }

  const result: SpaceList = {};

  savedSpaceIds.forEach(id => {
    const primary = saved[id].menuWidgetSettings?.primary;
    const cr = saved[id].menuWidgetSettings?.cr || [];

    if ((!primary || primary.length === 0) && cr.length === 0) return;

    const widgetsSettings: Partial<Record<ChartIds, WidgetSettings>> = {};
    primary?.forEach(settings => {
      widgetsSettings[settings.id] = {
        ...(settings.calcMethod ? {calcMethod: settings.calcMethod} : {}),
        ...(settings.isScaleChecked === undefined ? {} : {isScaleChecked: settings.isScaleChecked}),
      };
    });

    const isCrId = (cid: ChartIds) => String(cid).startsWith('funnel_cr_');

    const space = {
      id,
      title: saved[id].title,
      order: saved[id].order,
      widgetsBoardSelected: primary
        .filter(settings => settings.selectedInMenu && !isCrId(settings.id))
        .map(settings => settings.id),
      chartSelected: {
        current: primary
          .filter(settings => settings.isChartEnabled && !isCrId(settings.id))
          .map(settings => settings.id),
        previous: [],
      },
      widgetsMenuSelected: primary
        .filter(settings => settings.showInMenu && !isCrId(settings.id))
        .map(settings => settings.id),
      widgetsSettings,
    };

    // Восстановим CR-виджеты как динамические:
    const crIdsSet = new Set<string>();
    cr.forEach(crItem => {
      const crId = crItem.id as ChartIds;
      const aId = crItem.aGoalId;
      const bId = crItem.bGoalId;
      widgetsSettings[crId] = {
        ...(widgetsSettings[crId] || {}),
        custom: {
          label: 'CR, %',
          aGoalId: String(aId),
          bGoalId: String(bId),
        },
      };
      crIdsSet.add(String(crId));
      if (crItem.selectedInMenu) {
        space.widgetsBoardSelected.push(crId);
      }
      if (crItem.isChartEnabled) {
        space.chartSelected.current.push(crId);
      }
      if (!space.widgetsMenuSelected.includes(crId)) {
        space.widgetsMenuSelected.push(crId);
      }
    });

    // Нормализуем: удалим любые CR из списков, которые не присутствуют в сохранённых cr
    if (cr.length) {
      const notSavedCr = (id: ChartIds) =>
        String(id).startsWith('funnel_cr_') && !crIdsSet.has(String(id));
      space.widgetsBoardSelected = space.widgetsBoardSelected.filter(id => !notSavedCr(id));
      space.widgetsMenuSelected = space.widgetsMenuSelected.filter(id => !notSavedCr(id));
      space.chartSelected.current = space.chartSelected.current.filter(id => !notSavedCr(id));
    }

    result[id] = space;
  });

  // Нужно сортировать пространства в порядке добавления

  const sortedResult: SpaceList = {};

  savedSpaceIds
    .toSorted((a, b) => {
      const orderA = result[a]?.order ?? 0;
      const orderB = result[b]?.order ?? 0;
      return orderA - orderB;
    })
    .forEach(id => {
      sortedResult[id] = result[id];
    });

  return result;
};

export default parseSavedSpaces;
